import type { ApiAuthImportViewAccountResult, ApiChain, ApiNetwork } from '../../api/types';
import type { GlobalState } from '../types';
import { AppState } from '../types';

import { TEMPORARY_ACCOUNT_NAME } from '../../config';
import isEmptyObject from '../../util/isEmptyObject';
import { omit } from '../../util/iteratees';
import { getTranslation } from '../../util/langProvider';
import { callApi } from '../../api';
import { getActions, getGlobal, setGlobal } from '../index';
import { createAccount, markEmptyAccountLoaded, updateAccounts } from '../reducers';
import { selectCurrentAccountId, selectNetworkAccounts } from '../selectors';
import { dropEnclaveSessionHold, holdEnclaveSession } from './enclave';

/**
 * Adding a wallet stores the secret only at the end of the flow, after the user enters the phrase. The
 * multichain upgrade that the same password entry may start releases the session as soon as it finishes.
 * So the flow holds the session from the password entry until the secret is stored or the flow is reset.
 */
let accountCreationToken: string | undefined;

export function holdAccountCreationSession(token: string) {
  if (accountCreationToken === token) return;

  dropAccountCreationSession();
  accountCreationToken = token;
  holdEnclaveSession(token);
}

export function dropAccountCreationSession() {
  const token = accountCreationToken;
  if (!token) return;

  accountCreationToken = undefined;
  if (dropEnclaveSessionHold(token)) {
    getActions().releaseEnclaveSession({ enclaveToken: token });
  }
}

export async function removeTemporaryAccount(accountId: string) {
  const { currentAccountId } = getGlobal();
  // Don't pass the same accountId as nextAccountId - it will be deleted and can't be activated
  const nextAccountId = currentAccountId !== accountId ? currentAccountId : undefined;

  await callApi('removeAccount', accountId, nextAccountId);

  const global = getGlobal();
  const updatedGlobal = cleanupTemporaryAccountState(global, accountId);
  setGlobal(updatedGlobal);
}

/** Drops the given accounts from every account-keyed map, clearing `currentAccountId` if it is among them. */
export function omitAccounts(global: GlobalState, accountIds: string[]): GlobalState {
  if (!accountIds.length) return global;

  const { accounts, byAccountId, settings, currentAccountId } = global;
  const removed = new Set(accountIds);

  const newAccountsById = accounts ? omit(accounts.byId, accountIds) : undefined;
  const newByAccountId = omit(byAccountId, accountIds);
  const newSettingsByAccountId = omit(settings.byAccountId, accountIds);
  const orderedAccountIds = settings.orderedAccountIds?.filter((id) => !removed.has(id));

  return {
    ...global,
    currentAccountId: currentAccountId && removed.has(currentAccountId) ? undefined : currentAccountId,
    accounts: accounts && newAccountsById ? { ...accounts, byId: newAccountsById } : accounts,
    byAccountId: newByAccountId,
    settings: {
      ...settings,
      byAccountId: newSettingsByAccountId,
      orderedAccountIds,
    },
  };
}

function cleanupTemporaryAccountState(global: GlobalState, accountId: string): GlobalState {
  return {
    ...omitAccounts(global, [accountId]),
    currentTemporaryViewAccountId: undefined,
  };
}

export async function importTemporaryViewAccount(
  network: ApiNetwork,
  addressByChain: Partial<Record<ApiChain, string>>,
) {
  let global = getGlobal();
  global = updateAccounts(global, { isLoading: true });
  setGlobal(global);

  const result = await callApi('importViewAccount', network, addressByChain, true);

  global = getGlobal();
  global = updateAccounts(global, { isLoading: undefined });
  setGlobal(global);

  return result;
}

export function createAndSetTemporaryAccount(
  result: ApiAuthImportViewAccountResult,
  additionalUpdates?: Partial<GlobalState>,
): void {
  let global = getGlobal();
  global = createAccount({
    global,
    accountId: result.accountId,
    byChain: result.byChain,
    type: 'view',
    partial: {
      title: result.title || getTranslation(TEMPORARY_ACCOUNT_NAME),
      isTemporary: true,
    },
  });

  if (isEmptyObject(result.byChain)) {
    global = markEmptyAccountLoaded(global, result.accountId);
  }

  global = {
    ...global,
    currentTemporaryViewAccountId: result.accountId,
    ...additionalUpdates,
  };

  setGlobal(global);
}

export function finalizeAccountCreation(
  actions: ReturnType<typeof getActions>,
  accountId: string,
  shouldSwitchToWallet: boolean | undefined,
  switchingDuration: number,
  tokenSlug?: string,
): void {
  if (getGlobal().areSettingsOpen) {
    actions.closeSettings(undefined, { forceOnHeavyAnimation: true });
  }

  if (!shouldSwitchToWallet && !tokenSlug) return;

  window.setTimeout(() => {
    // Another account may become current during the delay, and the navigation belongs to the created one
    if (selectCurrentAccountId(getGlobal()) !== accountId) return;

    if (shouldSwitchToWallet) {
      actions.switchToWallet();
    }

    // `switchToWallet` returns the wallet to its home tab and closes a token screen opened before it
    if (tokenSlug) {
      actions.showTokenActivity({ slug: tokenSlug });
    }
  }, switchingDuration);
}

export function findExistingAccountByAddresses(
  global: GlobalState,
  addressByChain: Partial<Record<ApiChain, string>>,
): string | undefined {
  const accounts = selectNetworkAccounts(global);
  // Every account would match an empty address list
  if (!accounts || isEmptyObject(addressByChain)) return undefined;

  return Object.keys(accounts).find((accountId) => {
    const account = accounts[accountId];
    if (account.isTemporary) return false;

    return (Object.keys(addressByChain) as ApiChain[]).every(
      (chain) => account.byChain[chain]?.address === addressByChain[chain],
    );
  });
}

export async function handleExplorerMode(
  global: GlobalState,
  actions: ReturnType<typeof getActions>,
  network: ApiNetwork,
  addressByChain: Partial<Record<ApiChain, string>>,
  switchingDuration: number,
  tokenSlug?: string,
) {
  if (global.currentTemporaryViewAccountId) {
    await removeTemporaryAccount(global.currentTemporaryViewAccountId);
  }

  const result = await importTemporaryViewAccount(network, addressByChain);

  if (!result || 'error' in result) {
    actions.showError({ error: result?.error });
    return;
  }

  createAndSetTemporaryAccount(result, {
    currentAccountId: result.accountId,
    appState: AppState.Main,
  });

  finalizeAccountCreation(actions, result.accountId, true, switchingDuration, tokenSlug);
}

export async function handleStandardMode(
  global: GlobalState,
  actions: ReturnType<typeof getActions>,
  network: ApiNetwork,
  addressByChain: Partial<Record<ApiChain, string>>,
  switchingDuration: number,
  getIsPortrait: () => boolean | undefined,
  tokenSlug?: string,
) {
  const existingAccountId = findExistingAccountByAddresses(global, addressByChain);

  if (global.currentTemporaryViewAccountId) {
    await removeTemporaryAccount(global.currentTemporaryViewAccountId);
  }

  if (existingAccountId) {
    actions.switchAccount({ accountId: existingAccountId });
    return;
  }

  const result = await importTemporaryViewAccount(network, addressByChain);

  if (!result || 'error' in result) {
    actions.showError({ error: result?.error });
    return;
  }

  createAndSetTemporaryAccount(result);

  finalizeAccountCreation(actions, result.accountId, getIsPortrait(), switchingDuration, tokenSlug);
}
